// src/components/AccountHealth.js
import React from 'react';
import HealthScoreSparkline from './HealthScoreSparkline';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../constants/routes';
import { persistFilters } from './FilterControls';
import { ScoreTooltip } from './ScoreTooltips';

// Apollo-style design tokens
const apolloStyles = {
    transition: {
        fast: 'all 0.15s ease-in-out',
        normal: 'all 0.2s ease',
    },
    borderRadius: {
        default: 'var(--pallium-border-radius, 4px)',
        md: 'var(--pallium-border-radius-md, 6px)',
        lg: 'var(--pallium-border-radius-lg, 8px)',
        xl: '12px', // Apollo card pattern
    },
    shadow: {
        sm: 'var(--pallium-shadow-sm)',
        md: 'var(--pallium-shadow-md)',
    },
    focusRing: 'var(--pallium-focus-ring-primary, 0 0 0 0.25rem rgba(110, 79, 196, 0.25))',
};

// Get score-based background using color-mix pattern (Apollo style)
const getScoreBgColor = (score) => {
    if (score > 70) return 'color-mix(in srgb, var(--pallium-success-green) 12%, transparent)';
    if (score >= 40) return 'color-mix(in srgb, var(--pallium-warning-yellow) 12%, transparent)';
    return 'color-mix(in srgb, var(--pallium-error-red) 12%, transparent)';
};

// Get metric icon background using color-mix
const getMetricBgColor = (color) => {
    // Map CSS variable to color-mix
    if (color === 'var(--pallium-error-red)') return 'color-mix(in srgb, var(--pallium-error-red) 10%, transparent)';
    if (color === 'var(--pallium-warning-yellow)') return 'color-mix(in srgb, var(--pallium-warning-yellow) 10%, transparent)';
    if (color === 'var(--pallium-info-cyan)') return 'color-mix(in srgb, var(--pallium-info-cyan) 10%, transparent)';
    if (color === 'var(--pallium-primary-purple)') return 'var(--pallium-selected-option-bg, var(--pallium-purple-0-5))';
    return 'var(--pallium-bg-secondary)';
};

const AccountHealth = ({ 
    healthData, 
    healthTrend, 
    priorityCounts, 
    clientId 
}) => {
    const navigate = useNavigate();

    // Helper functions for health score
    const score = healthData?.score || 0;
    
    const getScoreColor = (score) => {
        if (score > 70) return 'var(--pallium-success-green)';
        if (score >= 40) return 'var(--pallium-warning-yellow)';
        return 'var(--pallium-error-red)';
    };

    const getScoreLabel = (score) => {
        if (score > 70) return 'Good Health';
        if (score >= 40) return 'Moderate Health';
        return 'At Risk';
    };

    // SVG circle calculation for health score
    const outerRadius = 52;
    const circumference = 2 * Math.PI * outerRadius;
    const scorePercentage = Math.min(100, Math.max(0, score));
    const strokeDashoffset = circumference - (scorePercentage / 100) * circumference;

    // Metric card component with Apollo styling
    const MetricCard = ({ onClick, icon, label, value, total, color, hoverColor }) => (
        <button 
            onClick={onClick}
            className="group flex items-center gap-3 p-3 text-left w-full"
            style={{ 
                backgroundColor: 'var(--pallium-bg-secondary)',
                border: '1px solid transparent',
                borderRadius: apolloStyles.borderRadius.lg,
                transition: apolloStyles.transition.fast,
            }}
            onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--pallium-bg-primary)';
                e.currentTarget.style.borderColor = hoverColor;
                e.currentTarget.style.boxShadow = apolloStyles.shadow.sm;
            }}
            onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--pallium-bg-secondary)';
                e.currentTarget.style.borderColor = 'transparent';
                e.currentTarget.style.boxShadow = 'none';
            }}
            onFocus={(e) => {
                e.currentTarget.style.boxShadow = apolloStyles.focusRing;
                e.currentTarget.style.outline = 'none';
            }}
            onBlur={(e) => {
                e.currentTarget.style.boxShadow = 'none';
            }}
        >
            <div 
                className="w-9 h-9 flex items-center justify-center flex-shrink-0"
                style={{ 
                    backgroundColor: getMetricBgColor(color), 
                    color: color,
                    borderRadius: apolloStyles.borderRadius.md
                }}
            >
                {icon}
            </div>
            <div className="flex flex-col min-w-0">
                <span 
                    className="text-[10px] font-semibold uppercase tracking-wider leading-tight" 
                    style={{ color: 'var(--pallium-text-secondary)' }}
                >
                    {label}
                </span>
                <div className="flex items-baseline gap-1">
                    <span className="text-xl font-bold" style={{ color }}>
                        {value}
                    </span>
                    {total !== undefined && (
                        <span className="text-sm" style={{ color: 'var(--pallium-text-secondary)' }}>/ {total}</span>
                    )}
                </div>
            </div>
        </button>
    );

    return (
        <div 
            className="mb-6"
            style={{ 
                backgroundColor: 'var(--pallium-bg-primary)', 
                boxShadow: apolloStyles.shadow.md,
                borderRadius: apolloStyles.borderRadius.xl,
                // Apollo card pattern: no border, shadow-based
            }}
        >
            {/* Header */}
            <div 
                className="px-6 py-4"
                style={{ borderBottom: '1px solid var(--pallium-border-subtle)' }}
            >
                <h2 className="text-lg font-semibold flex items-center gap-1" style={{ color: 'var(--pallium-text-primary)' }}>
                    Account Health
                    <ScoreTooltip type="healthScore" size="md" />
                </h2>
            </div>

            {/* Content */}
            <div className="p-6">
                <div className="flex items-stretch gap-6">
                    {/* Left Section: Health Score + Trend */}
                    <div 
                        className="flex items-center gap-6 pr-6 flex-shrink-0"
                        style={{ borderRight: '1px solid var(--pallium-border-subtle)' }}
                    >
                        {/* Health Score Circle */}
                        <div className="relative w-32 h-32">
                            <svg className="w-full h-full transform -rotate-90" viewBox="0 0 128 128">
                                {/* Background circle */}
                                <circle
                                    cx="64"
                                    cy="64"
                                    r={outerRadius}
                                    stroke="var(--pallium-border-subtle)"
                                    strokeWidth="8"
                                    fill="none"
                                />
                                {/* Progress circle with smooth transition */}
                                <circle
                                    cx="64"
                                    cy="64"
                                    r={outerRadius}
                                    stroke={getScoreColor(score)}
                                    strokeWidth="8"
                                    fill="none"
                                    strokeDasharray={circumference}
                                    strokeDashoffset={strokeDashoffset}
                                    strokeLinecap="round"
                                    style={{ transition: 'stroke-dashoffset 0.5s ease, stroke 0.3s ease' }}
                                />
                            </svg>
                            <div className="absolute inset-0 flex items-center justify-center">
                            <span 
                                className="text-2xl font-bold" 
                                style={{ color: 'var(--pallium-text-primary)' }}
                                data-testid="health-score"
                            >
                                {Math.round(score)}%
                            </span>
                            </div>
                        </div>

                        {/* Trend Section */}
                        <div className="flex flex-col items-center" data-testid="trend-line">
                            <span 
                                className="text-xs font-medium mb-1" 
                                style={{ color: 'var(--pallium-text-secondary)' }}
                            >
                                90-Day Trend
                            </span>
                            {healthTrend && healthTrend.length > 0 ? (
                                <HealthScoreSparkline data={healthTrend} width={120} height={40} />
                            ) : (
                                <div 
                                    className="text-xs py-2" 
                                    style={{ color: 'var(--pallium-text-disabled)' }}
                                >
                                    No trend data
                                </div>
                            )}
                            {/* Status badge using Apollo color-mix pattern */}
                            <div 
                                className="mt-2 px-3 py-1 text-xs font-semibold"
                                style={{ 
                                    backgroundColor: getScoreBgColor(score),
                                    color: getScoreColor(score),
                                    borderRadius: '9999px' // pill shape
                                }}
                            >
                                {getScoreLabel(score)}
                            </div>
                        </div>
                    </div>

                    {/* Right Section: Quick Metrics Grid */}
                    <div className="flex-1 grid grid-cols-2 gap-3 content-center">
                        {/* Issues */}
                        <div data-testid="high-sev-issues">
                        <MetricCard
                            onClick={() => {
                                navigate(`${ROUTES.ACCOUNTS}/${clientId}/issues`);
                            }}
                            icon={
                                <svg className="w-4 h-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                            }
                            label="Issues"
                            value={priorityCounts.allOpenIssues}
                            color="var(--pallium-error-red)"
                            hoverColor="var(--pallium-error-red)"
                        />
                        </div>

                        {/* Initiatives */}
                        <div data-testid="blocked-inits">
                        <MetricCard
                            onClick={() => {
                                persistFilters({ initiativeStatusFilter: '' });
                                navigate(`${ROUTES.ACCOUNTS}/${clientId}/goals`);
                            }}
                            icon={
                                <svg className="w-4 h-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 15a1 1 0 011-1h6a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                                </svg>
                            }
                            label="Initiatives"
                            value={priorityCounts.allOpenInitiatives}
                            color="var(--pallium-warning-yellow)"
                            hoverColor="var(--pallium-warning-yellow)"
                        />
                        </div>

                        {/* Action Items */}
                        <div data-testid="overdue-actions">
                        <MetricCard
                            onClick={() => {
                                navigate(`${ROUTES.ACCOUNTS}/${clientId}/action-items`);
                            }}
                            icon={
                                <svg className="w-4 h-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                                </svg>
                            }
                            label="Action Items"
                            value={priorityCounts.allOpenActionItems}
                            color="var(--pallium-info-cyan)"
                            hoverColor="var(--pallium-info-cyan)"
                        />
                        </div>

                        {/* Goals */}
                        <div data-testid="active-goals">
                        <MetricCard
                            onClick={() => {
                                persistFilters({ statusFilter: 'open', showBlockedInitiatives: false, initiativeStatusFilter: '' });
                                navigate(`${ROUTES.ACCOUNTS}/${clientId}/goals`);
                            }}
                            icon={
                                <svg className="w-4 h-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                            }
                            label="Goals"
                            value={priorityCounts.allOpenGoals}
                            color="var(--pallium-primary-purple)"
                            hoverColor="var(--pallium-primary-purple)"
                        />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AccountHealth;
